import React, { useState } from 'react';
import { formatReturnsCentered, returnsToCentered, formatExpectedReturn, toneOfSigned } from '../utils/returns';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';

const formatDate = (date) => {
  if (!date) return 'N/A';
  // Create date object and adjust for timezone
  const dateObj = new Date(date);
  const timezoneOffset = dateObj.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
  const adjustedDate = new Date(dateObj.getTime() + timezoneOffset);
  
  return adjustedDate.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    timeZone: 'UTC' // Force UTC to prevent double timezone conversion
  });
};

export default function PlaybookCard({ 
  playbook, 
  isActive, 
  onClick, 
  onView, 
  onDelete,
  onRefresh,
  deleteConfirm, 
  showActions = false,
  isRefreshing = false,
  isPublicView = false,
  creatorType,
  creatorName,
  avgAccuracy,
  lastEventReturn
}) {
  const [refreshConfirm, setRefreshConfirm] = useState(null);
  
  // Check if event date is older than today minus 1 day
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const isEventOld = playbook.event_date ? new Date(playbook.event_date) < yesterday : false;
  
  // Check if last updated is older than today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lastUpdated = playbook.last_updated ? new Date(playbook.last_updated) : null;
  const needsUpdate = lastUpdated ? lastUpdated < today : false;
  
  // Show refresh button if either condition is met
  const showRefresh = isEventOld || needsUpdate;

  // Handle refresh click with confirmation
  const handleRefreshClick = (e) => {
    e.stopPropagation();
    if (refreshConfirm === playbook.id) {
      onRefresh?.(playbook.id);
      setRefreshConfirm(null);
    } else {
      setRefreshConfirm(playbook.id);
    }
  };

  return (
    <div 
      className={`playbook-card ${isActive ? 'active' : ''} ${!showActions && !isPublicView ? 'selectable' : ''}`}
      onClick={onClick}
    >
      <div className="playbook-header">
        <div>
          <div className="playbook-title-row">
            <h3 className="playbook-title">{playbook.name || 'Untitled Playbook'}</h3>
            {isPublicView && playbook.is_public !== undefined && (
              <span className="visibility-badge">
                {playbook.is_public ? (
                  <PublicIcon sx={{ fontSize: 16 }} />
                ) : (
                  <LockIcon sx={{ fontSize: 16 }} />
                )}
              </span>
            )}
          </div>
          {isPublicView && creatorType && (
            <div className="creator-badge">
              {creatorType === 'special' && <StarIcon sx={{ fontSize: 14 }} />}
              <span>{creatorName || 'Unknown'}</span>
              {creatorType === 'odikai' && <span className="badge-label">Odikai</span>}
              {creatorType === 'special' && <span className="badge-label">Featured</span>}
            </div>
          )}
          {playbook.event && (
            <div className="playbook-event">
              <EventIcon sx={{ fontSize: 16 }} />
              {playbook.event.name}
            </div>
          )}
        </div>
        {showActions && (
          <button 
            className="view-icon" 
            aria-label="View playbook"
            title="View playbook"
            onClick={(e) => {
              e.stopPropagation();
              onView(playbook.id);
            }}
            disabled={isRefreshing}
          >
            <VisibilityIcon sx={{ fontSize: 20 }} />
          </button>
        )}
      </div>

      <div className="playbook-stats">
        {isPublicView && avgAccuracy !== undefined && (
          <div className="stat-row highlight-stat">
            <span className="stat-label">Average accuracy</span>
            <span className="stat-value">{(avgAccuracy * 100).toFixed(1)}%</span>
          </div>
        )}
        {isPublicView && lastEventReturn !== undefined && (
          <div className="stat-row highlight-stat">
            <span className="stat-label">Last event return</span>
            <span className={`stat-value ${(returnsToCentered(lastEventReturn) ?? -1) >= 0 ? 'positive' : 'negative'}`}>
              {formatReturnsCentered(lastEventReturn)}
            </span>
          </div>
        )}
        <div className="stat-row">
          <span className="stat-label">Event</span>
          <span className="stat-value">{playbook.event_name || 'No event'}</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Event date</span>
          <span className="stat-value">{formatDate(playbook.event_date)}</span>
        </div>
        {!isPublicView && (
          <>
            <div className="stat-row">
              <span className="stat-label">Updated</span>
              <span className="stat-value">{formatDate(playbook.last_updated)}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Picks</span>
              <span className="stat-value">{playbook.picks?.length || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Expected yearly return</span>
              <span className={`stat-value ${toneOfSigned(playbook.portfolio_returns)}`}>
                {formatExpectedReturn(playbook.portfolio_returns)}
              </span>
            </div>
          </>
        )}
      </div>

      {isPublicView && (
        <div className="view-playbook-button-wrapper">
          <button 
            className="view-playbook-button"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(playbook.id);
            }}
          >
            View playbook
          </button>
        </div>
      )}

      {showActions && (
        <div className="playbook-actions">
          {showRefresh && (
            <button 
              className={`action-button refresh-button ${refreshConfirm === playbook.id ? 'confirm' : ''}`}
              onClick={handleRefreshClick}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <CircularProgress size={20} color="inherit" /> Refreshing...
                </>
              ) : refreshConfirm === playbook.id ? (
                <>
                  <WarningIcon /> Confirm refresh
                </>
              ) : (
                <>
                  <RefreshIcon /> Refresh picks
                </>
              )}
            </button>
          )}
          <button 
            className={`action-button delete-button ${deleteConfirm === playbook.id ? 'confirm' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(playbook.id);
            }}
            disabled={isRefreshing}
          >
            {deleteConfirm === playbook.id ? (
              <>
                <WarningIcon /> Confirm delete
              </>
            ) : (
              <>
                <DeleteIcon /> Delete
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
} 